import { inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { Router } from '@angular/router';
import { CalendarView, UserSettings } from '@models';
import { RoutesService, SettingsService, UserService } from '@services';
import { logError, matchBelow } from '@shared/utils';
import {
  AppState,
  onSessionEnd,
  resetUser,
  setIsLoggedIn,
  setSessionToken,
  setSidebarVisibility,
  setUserData,
} from '@store';

@Service()
export class UserBeanService {
  router = inject(Router);
  settingsService = inject(SettingsService);
  store = inject<Store<AppState>>(Store);
  routesService = inject(RoutesService);
  userService = inject(UserService);

  user!: UserSettings;
  auth!: { sessionToken?: Nullable<string> };
  cachedPromise: Nullable<Promise<UserSettings>> = null;

  constructor() {
    onSessionEnd(() => {
      this.cachedPromise = null;
      this.lastFailureTime = 0;
    });
    this.store
      .pipe(
        select((state) => state),
        takeUntilDestroyed()
      )
      .subscribe((state) => {
        this.user = state.user;
        this.auth = state.auth;
      });
  }

  setUserData = (userBean: UserSettings) => {
    this.store.dispatch(setUserData({ payload: userBean }));

    if (userBean.preferences) {
      this.store.dispatch(
        setSidebarVisibility({
          payload: matchBelow('md').matches
            ? false
            : (userBean.preferences.sidebar ?? true),
        })
      );
    }

    this.settingsService.setRootData(userBean);
  };

  resetData = () => {
    this.store.dispatch(
      setIsLoggedIn({
        payload: false,
      })
    );
    this.store.dispatch(resetUser());
    this.store.dispatch(setSessionToken({ payload: null }));
    this.settingsService.resetRootData();
  };

  setUserBean = (userBean: UserSettings) => {
    this.cachedPromise = null;
    this.lastFailureTime = 0;
    this.setUserData(userBean);
  };

  resetUserBean = () => this.resetData();

  getAuthToken = () => this.auth?.sessionToken;

  lastFailureTime = 0;
  readonly FAILURE_CACHE_DURATION = 2000;

  getUserBean = () => {
    if (this.cachedPromise) {
      return this.cachedPromise;
    }

    if (this.user?.userId) {
      return Promise.resolve(this.user);
    }

    const now = Date.now();

    if (now - this.lastFailureTime < this.FAILURE_CACHE_DURATION) {
      return Promise.reject(new Error('Authentication recently failed'));
    }

    this.cachedPromise = firstValueFrom(this.userService.getUserSettings())
      .then((response) => {
        this.store.dispatch(
          setSessionToken({
            payload: response.access_token || null,
          })
        );
        this.store.dispatch(
          setIsLoggedIn({
            payload: true,
          })
        );
        this.setUserData(response);

        if (response.mustAcceptConditions) {
          this.router.navigate(['/login']);
          throw new Error('Community conditions must be accepted');
        }

        setTimeout(() => {
          if (this.cachedPromise) {
            this.cachedPromise = null;
          }
        }, 0);

        return response;
      })
      .catch((error) => {
        this.resetData();

        this.lastFailureTime = Date.now();

        setTimeout(() => {
          if (this.cachedPromise) {
            this.cachedPromise = null;
          }
        }, this.FAILURE_CACHE_DURATION);

        throw error;
      });

    return this.cachedPromise;
  };

  refreshUserBean = () =>
    firstValueFrom(this.userService.getUserSettings())
      .then((response) => {
        this.setUserData(response);

        return response;
      })
      .catch((err) => logError(err));

  setRoomDefaultView = (view: number) =>
    firstValueFrom(
      this.userService.updateUserPreferences({ defaultView: view })
    )
      .then((data) => {
        this.settingsService.setRoomDefaultView(view);

        return data;
      })
      .catch((err) => logError(err));

  setSidebarVisibility = (isVisible: boolean) =>
    matchBelow('md').matches
      ? Promise.resolve()
      : firstValueFrom(
          this.userService.updateUserPreferences({ sidebar: isVisible })
        );

  getRoomDefaultView = () => this.settingsService.getRoomDefaultView();

  setUsersView = (view: number) =>
    firstValueFrom(this.userService.updateUserPreferences({ usersView: view }))
      .then((data) => {
        this.settingsService.setUsersView(view);

        return data;
      })
      .catch((error) => logError(error));

  setGroupsView = (view: number) =>
    firstValueFrom(this.userService.updateUserPreferences({ groupsView: view }))
      .then((data) => {
        this.settingsService.setGroupsView(view);

        return data;
      })
      .catch((err) => logError(err));

  setCalendarDefaultView = (view: CalendarView) =>
    firstValueFrom(
      this.userService.updateUserPreferences({
        calendarDefaultView: view,
      })
    )
      .then((data) => {
        this.settingsService.setCalendarDefaultView(view);

        return data;
      })
      .catch((err) => logError(err));

  fetchUserConfig = <T = Record<string, unknown>>(isGuest = false) =>
    firstValueFrom(
      this.userService.getUserConfig<T>(this.user.rootUsersId, isGuest)
    );

  keepAlive = () =>
    firstValueFrom(this.userService.getUserAlive())
      .then((res) => res)
      .catch(() => this.routesService.getPreviousRouteQueryParams());
}
